<template>
  <div class="flex flex-col gap-4" data-testid="pm-archived-projects-tab">
    <div class="flex justify-end gap-2">
      <Button
        :label="$t('ProjectManagement.archivedProjects.refresh')"
        icon="pi pi-refresh"
        severity="secondary"
        :loading="loading"
        data-testid="pm-archived-refresh"
        @click="refresh"
      />
    </div>

    <Message v-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div v-if="loading" class="flex items-center justify-center py-16 text-[var(--color-disabled)]">
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('ProjectManagement.archivedProjects.loading') }}
    </div>

    <div
      v-else-if="isEmpty"
      class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
      data-testid="pm-archived-empty"
    >
      <div class="font-medium text-[var(--color-dark)]">{{ $t('ProjectManagement.archivedProjects.emptyTitle') }}</div>
      <div class="text-sm mt-1">{{ $t('ProjectManagement.archivedProjects.emptyHint') }}</div>
    </div>

    <DataTable v-else :value="projects" data-key="id" striped-rows>
      <Column field="name" :header="$t('ProjectManagement.columns.name')" />
      <Column :header="$t('ProjectManagement.columns.status')">
        <template #body="{ data }">
          {{ $t(`ProjectManagement.status.${data.status}`) }}
        </template>
      </Column>
      <Column :header="$t('ProjectManagement.columns.lead')">
        <template #body="{ data }">
          {{ userDisplayName(data.projectLeadUserId) }}
        </template>
      </Column>
      <Column :header="$t('ProjectManagement.columns.teams')">
        <template #body="{ data }">
          {{ teamDisplayNames(data.teams) }}
        </template>
      </Column>
      <Column :header="$t('ProjectManagement.columns.actions')">
        <template #body="{ data }">
          <Button
            icon="pi pi-eye"
            severity="secondary"
            text
            rounded
            :aria-label="$t('ProjectManagement.archivedProjects.viewHistory')"
            data-testid="pm-view-archived-project"
            @click="openDetail(data)"
          />
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="detailVisible"
      modal
      :header="detailProject?.name || $t('ProjectManagement.archivedProjects.detailTitle')"
      class="w-full max-w-6xl"
      data-testid="pm-archived-detail-dialog"
      @hide="closeDetail"
    >
      <div v-if="detailLoading" class="flex items-center justify-center py-12 text-[var(--color-disabled)]">
        <i class="pi pi-spin pi-spinner mr-2" />
        {{ $t('ProjectManagement.archivedProjects.detailLoading') }}
      </div>

      <ArchiveProjectDetail
        v-else-if="detailProject"
        :project="detailProject"
        :summary="summary"
        :commitments="commitments"
        :constraints="constraints"
        :decisions="decisions"
        :milestones="milestones"
      />
    </Dialog>
  </div>
</template>

<script>
import { Button, Message, DataTable, Column, Dialog } from 'primevue';
import pmProjectService from '@/services/pmProjectService';
import pmCommitmentService from '@/services/pmCommitmentService';
import pmConstraintService from '@/services/pmConstraintService';
import pmDecisionService from '@/services/pmDecisionService';
import pmMilestoneService from '@/services/pmMilestoneService';
import ArchiveProjectDetail from '@/components/CustomerApp/UserProjects/ArchiveProjectDetail.vue';
import { buildArchiveSummary } from '@/utils/userProjectsArchive';

export default {
  name: 'ArchivedProjectsPanel',
  components: {
    Button,
    Message,
    DataTable,
    Column,
    Dialog,
    ArchiveProjectDetail,
  },
  props: {
    userDisplayName: {
      type: Function,
      required: true,
    },
    teamDisplayNames: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      projects: [],
      loading: false,
      error: null,
      detailVisible: false,
      detailLoading: false,
      detailProject: null,
      commitments: [],
      constraints: [],
      decisions: [],
      milestones: [],
      summary: buildArchiveSummary(),
    };
  },
  computed: {
    isEmpty() {
      return !this.loading && !this.error && this.projects.length === 0;
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      this.error = null;
      try {
        const data = await pmProjectService.listProjects({ scope: 'archived' });
        this.projects = data.items || [];
      } catch (error) {
        this.error = error?.response?.data?.message || error?.message || this.$t('ProjectManagement.archivedProjects.error');
        this.projects = [];
      } finally {
        this.loading = false;
      }
    },

    async openDetail(project) {
      this.detailProject = project;
      this.detailVisible = true;
      this.detailLoading = true;
      try {
        const [commitmentsData, constraintsData, decisionsData, milestonesData] = await Promise.all([
          pmCommitmentService.listCommitments(project.id),
          pmConstraintService.listConstraints(project.id),
          pmDecisionService.listDecisions(project.id),
          pmMilestoneService.listMilestones(project.id),
        ]);

        this.commitments = commitmentsData.items || [];
        this.constraints = constraintsData.items || [];
        this.decisions = decisionsData.items || [];
        this.milestones = milestonesData.items || [];
        this.summary = buildArchiveSummary({
          commitments: this.commitments,
          constraints: this.constraints,
          decisions: this.decisions,
          milestones: this.milestones,
        });
      } catch (error) {
        this.error = error?.response?.data?.message || error?.message || this.$t('ProjectManagement.archivedProjects.error');
        this.commitments = [];
        this.constraints = [];
        this.decisions = [];
        this.milestones = [];
        this.summary = buildArchiveSummary();
      } finally {
        this.detailLoading = false;
      }
    },

    closeDetail() {
      this.detailVisible = false;
      this.detailProject = null;
      this.commitments = [];
      this.constraints = [];
      this.decisions = [];
      this.milestones = [];
      this.summary = buildArchiveSummary();
    },
  },
};
</script>
